import { Button } from "@ui/Button";
import { formatDurationClockShort } from "@ui/format";
import { usePendingImage } from "@ui/hooks/usePendingImage";
import { Loader } from "@ui/Loader";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Pill } from "@ui/Pill";
import { HStack, VStack } from "@ui/Stack";
import { MonoText } from "@ui/typography/MonoText";
import { Text } from "@ui/typography/Text";
import {
	createEffect,
	createMemo,
	createSignal,
	For,
	onCleanup,
	Show,
	untrack,
} from "solid-js";
import { createStore } from "solid-js/store";
import {
	isQuestionBoxVideoClip,
	type QuestionBoxVideoMentionData,
	questionBoxVideoMentionKey,
} from "./QuestionBoxVideoMention";
import type { QuestionContextListProps } from "./questionContextKeyboard";
import {
	QUESTION_CONTEXT_VIDEO_PAGE_SIZE,
	type QuestionContextVideoSearch,
} from "./questionContextVideoSearch";
import { isRecentVideoMention } from "./RecentVideoMentions";
import "./QuestionContextVideos.css";

export type QuestionContextVideo = {
	id: string;
	title: string;
	thumbnailUrl?: string | null;
	durationSec?: number | null;
	handle?: string | null;
	startTimeSec?: number | null;
	endTimeSec?: number | null;
};

/** How long the typed handle must stand still before it is searched for. */
const SEARCH_DEBOUNCE_MS = 250;

type Props = QuestionContextListProps & {
	/** Clips the conversation has mentioned so far. */
	videos: QuestionContextVideo[];
	/**
	 * The `@handle` being written, to search the pool's videos for, or "" for
	 * none.
	 */
	searchQuery: string;
	/**
	 * Whether "" means "browse the pool" rather than "search for nothing". Set
	 * for a box that starts a conversation, whose tab would otherwise stand
	 * empty until a handle was typed (see QuestionBoxProps.contextSource).
	 */
	browsesPool: boolean;
	/**
	 * Whether this is the tab on screen. Videos are searched for only once it is,
	 * and what was found stays while it isn't: a tab nobody is looking at should
	 * neither search again nor throw away what it has.
	 */
	active: boolean;
	searchVideos: QuestionContextVideoSearch;
	insertedVideoMentionKeys: () => string[];
	insertVideoMentions: (mentions: QuestionBoxVideoMentionData[]) => void;
	canAddMore?: () => boolean;
};

/** A row, and where it came from — which is what its second line says. */
type VideoRowItem = {
	video: QuestionContextVideo;
	mentioned: boolean;
};

type QueryCache = {
	videoIds: string[];
	total: number;
};

const durationLabel = (seconds: number | null | undefined) => {
	if (seconds == null || !Number.isFinite(seconds)) {
		return "0:00";
	}
	return formatDurationClockShort(seconds);
};

const handleLabel = (handle: string | null | undefined) => {
	if (!handle) {
		return "@account";
	}
	return handle.startsWith("@") ? handle : `@${handle}`;
};

const subtitle = (row: VideoRowItem) => {
	const parts = [
		durationLabel(row.video.durationSec),
		handleLabel(row.video.handle),
	];
	if (isRecentVideoMention(row.video.id)) {
		parts.push("Recent");
	}
	return parts.join(" · ");
};

const VideoRow = (props: {
	video: QuestionContextVideo;
	subtitle: string;
	mentioned: boolean;
	added: boolean;
	blocked: boolean;
	highlighted: boolean;
	onSelect: () => void;
}) => {
	let rowRef: HTMLDivElement | undefined;
	const thumbnail = usePendingImage(() => props.video.thumbnailUrl);

	createEffect(() => {
		if (props.highlighted) rowRef?.scrollIntoView({ block: "nearest" });
	});

	return (
		<div
			ref={rowRef}
			class="gvtui-question-context-videos__item"
			classList={{
				"gvtui-question-context-videos__item--highlighted": props.highlighted,
				"gvtui-question-context-videos__item--blocked": props.blocked,
			}}
			role="button"
			tabIndex={props.highlighted ? 0 : -1}
			aria-disabled={props.blocked || undefined}
			aria-label={
				props.added
					? `${props.video.title} already in question`
					: props.blocked
						? "Can't add more videos"
						: `Add ${props.video.title}`
			}
			onMouseDown={(event) => event.preventDefault()}
			onClick={() => {
				if (!props.blocked) {
					props.onSelect();
				}
			}}
			onKeyDown={(event) => {
				if (props.blocked) {
					return;
				}
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					props.onSelect();
				}
			}}
		>
			<HStack fullWidth align="center" justify="space-between" spacing="medium">
				<HStack grow align="center" spacing="medium">
					<Show when={thumbnail.src()}>
						{(src) => (
							<img
								class="gvtui-question-context-videos__thumbnail"
								src={src()}
								alt=""
							/>
						)}
					</Show>
					<VStack grow>
						<Text color="inherit" ellipsis weight="bold">
							{props.video.title}
						</Text>
						<HStack align="center" spacing="small">
							<Show when={isQuestionBoxVideoClip(props.video)}>
								<MaterialSymbol
									symbol="content_cut"
									color="purple"
									relativeSize={0.9}
								/>
							</Show>
							<MonoText color="fg3">{props.subtitle}</MonoText>
							<Show when={props.mentioned}>
								<Pill theme="grey600" size="xsmall" variant="outline">
									Mentioned
								</Pill>
							</Show>
						</HStack>
					</VStack>
				</HStack>
				<MaterialSymbol
					symbol={props.added ? "check" : "add"}
					color={props.added ? "success" : "fg3"}
					relativeSize="larger"
				/>
			</HStack>
		</div>
	);
};

export const QuestionContextVideos = (props: Props) => {
	const [searchedQuery, setSearchedQuery] = createSignal("");
	const [hasBeenShown, setHasBeenShown] = createSignal(false);
	const [videosById, setVideosById] = createStore<
		Record<string, QuestionContextVideo>
	>({});
	const [byQuery, setByQuery] = createStore<Record<string, QueryCache>>({});
	const [loadingInitial, setLoadingInitial] = createSignal(false);
	const [loadingMore, setLoadingMore] = createSignal(false);
	let searchTimer: ReturnType<typeof setTimeout> | undefined;

	createEffect(() => {
		if (!props.active) return;
		const query = props.searchQuery.trim();
		const firstVisit = !untrack(hasBeenShown);
		clearTimeout(searchTimer);
		if (query !== untrack(searchedQuery)) {
			if (firstVisit || query.length === 0) {
				setSearchedQuery(query);
			} else {
				searchTimer = setTimeout(
					() => setSearchedQuery(query),
					SEARCH_DEBOUNCE_MS,
				);
			}
		}
		setHasBeenShown(true);
	});

	onCleanup(() => clearTimeout(searchTimer));

	const searchSource = createMemo(() => {
		if (!hasBeenShown()) return null;
		const query = searchedQuery();
		if (query.length === 0 && !props.browsesPool) return null;
		return { query };
	});

	const mergeSearchPage = (
		query: string,
		page: { videos: QuestionContextVideo[]; total: number },
		append: boolean,
	) => {
		const newIds = page.videos.map((video) => video.id);
		for (const video of page.videos) {
			setVideosById(video.id, video);
		}
		setByQuery(query, (prev) => {
			if (!append || !prev) {
				return { videoIds: newIds, total: page.total };
			}
			const seen = new Set(prev.videoIds);
			const videoIds = [...prev.videoIds];
			for (const id of newIds) {
				if (seen.has(id)) continue;
				seen.add(id);
				videoIds.push(id);
			}
			return { videoIds, total: page.total };
		});
	};

	createEffect(() => {
		const source = searchSource();
		if (!source) return;
		const query = source.query;
		if (byQuery[query]) return;

		setLoadingInitial(true);
		void props
			.searchVideos(query, 1)
			.then((page) => {
				if (query !== searchedQuery()) return;
				mergeSearchPage(query, page, false);
			})
			.catch((err) => {
				console.error("video search failed", err);
			})
			.finally(() => {
				if (query === searchedQuery()) {
					setLoadingInitial(false);
				}
			});
	});

	const searchResults = createMemo(() => {
		const query = searchedQuery();
		const bucket = byQuery[query];
		if (!bucket) return [];
		return bucket.videoIds
			.map((id) => videosById[id])
			.filter((video): video is QuestionContextVideo => video !== undefined);
	});

	const mentionedVideos = () => {
		const query = props.searchQuery.trim().toLowerCase();
		if (query.length === 0) return props.videos;
		return props.videos.filter((video) =>
			video.title.toLowerCase().includes(query),
		);
	};

	const rows = createMemo<VideoRowItem[]>(() => {
		const items: VideoRowItem[] = mentionedVideos().map((video) => ({
			video,
			mentioned: true,
		}));
		const seen = new Set(
			items.map((item) => questionBoxVideoMentionKey(item.video)),
		);
		for (const video of searchResults()) {
			const key = questionBoxVideoMentionKey(video);
			if (seen.has(key)) continue;
			seen.add(key);
			items.push({ video, mentioned: false });
		}
		return items;
	});

	const hasMore = () => {
		const query = searchedQuery();
		const bucket = byQuery[query];
		if (!bucket) return false;
		return bucket.videoIds.length < bucket.total;
	};

	const loadMore = async () => {
		const query = searchedQuery();
		const bucket = byQuery[query];
		if (!bucket || loadingMore() || !hasMore()) return;

		const nextPage =
			Math.floor(bucket.videoIds.length / QUESTION_CONTEXT_VIDEO_PAGE_SIZE) + 1;
		setLoadingMore(true);
		try {
			const page = await props.searchVideos(query, nextPage);
			if (query !== searchedQuery()) return;
			mergeSearchPage(query, page, true);
		} catch (err) {
			console.error("video search load more failed", err);
		} finally {
			setLoadingMore(false);
		}
	};

	const isInserted = (video: QuestionContextVideo) =>
		props
			.insertedVideoMentionKeys()
			.includes(questionBoxVideoMentionKey(video));

	const canAddMore = () => props.canAddMore?.() ?? true;

	const addVideo = (video: QuestionContextVideo) => {
		if (isInserted(video) || !canAddMore()) {
			return;
		}
		props.insertVideoMentions([
			{
				id: video.id,
				label: video.title,
				thumbnailUrl: video.thumbnailUrl,
				startTimeSec: video.startTimeSec,
				endTimeSec: video.endTimeSec,
			},
		]);
	};

	props.registerList({
		count: () => rows().length,
		select: (index) => {
			const row = rows()[index];
			if (row) addVideo(row.video);
		},
	});

	const showLoader = () =>
		searchSource() !== null && loadingInitial() && !byQuery[searchedQuery()];

	return (
		<Show
			when={rows().length > 0 || !showLoader()}
			fallback={
				<HStack fullWidth justify="center">
					<Loader />
				</HStack>
			}
		>
			<VStack fullWidth spacing="none">
				<For each={rows()}>
					{(row, index) => (
						<VideoRow
							video={row.video}
							subtitle={subtitle(row)}
							mentioned={row.mentioned}
							added={isInserted(row.video)}
							blocked={!isInserted(row.video) && !canAddMore()}
							highlighted={index() === props.highlightedIndex}
							onSelect={() => addVideo(row.video)}
						/>
					)}
				</For>
				<Show
					when={searchSource() !== null && rows().length === 0 && !showLoader()}
				>
					<HStack fullWidth justify="center">
						<Text color="fg3">
							{searchedQuery().length > 0
								? "No videos match"
								: "No videos to add"}
						</Text>
					</HStack>
				</Show>
				<Show when={hasMore()}>
					<HStack
						class="gvtui-question-context-videos__item"
						fullWidth
						justify="center"
					>
						<Button
							variant="text"
							symbol={loadingMore() ? undefined : "expand_more"}
							disabled={loadingMore()}
							aria-busy={loadingMore()}
							aria-label="Load more videos"
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => void loadMore()}
						>
							<Show when={loadingMore()}>
								<Loader size="xsmall" />
							</Show>
							Load more
						</Button>
					</HStack>
				</Show>
			</VStack>
		</Show>
	);
};
